import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Search, SlidersHorizontal, Frown, LayoutGrid, Map as MapIcon, Check, BedDouble } from "lucide-react";
import { INDIA_STATES, STATE_DISTRICTS } from "@/data/indiaLocations";
import PropertyMap from "@/components/PropertyMap";

const TYPES = ["Apartment", "Villa", "Independent House", "Plot", "Commercial"];
const BHKS = ["1", "2", "3", "4", "5"];
const ANY = "any";

export default function Listings() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);

  const [listingType, setListingType] = useState(searchParams.get("type") || "buy");
  const [state, setState] = useState(searchParams.get("state") || ANY);
  const [city, setCity] = useState(searchParams.get("city") || ANY);
  const [propType, setPropType] = useState(ANY);
  const [bhk, setBhk] = useState(ANY);
  const [minBath, setMinBath] = useState(ANY);
  const [minCar, setMinCar] = useState(ANY);
  const [ensuite, setEnsuite] = useState(false);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [maxPrice, setMaxPrice] = useState("");
  const [view, setView] = useState("list");

  const districts = state !== ANY ? (STATE_DISTRICTS[state] || []) : [];

  const fetchData = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (listingType) params.listing_type = listingType;
    if (state !== ANY) params.state = state;
    if (city !== ANY) params.city = city;
    if (propType !== ANY) params.property_type = propType;
    if (bhk !== ANY) params.bhk = bhk;
    if (minBath !== ANY) params.min_bathrooms = minBath;
    if (minCar !== ANY) params.min_car_spaces = minCar;
    if (ensuite) params.ensuite = true;
    if (q) params.q = q;
    if (maxPrice) params.max_price = maxPrice;
    const res = await api.get("/properties", { params });
    setProperties(res.data);
    setLoading(false);
  }, [listingType, state, city, propType, bhk, minBath, minCar, ensuite, q, maxPrice]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onStateChange = (v) => { setState(v); setCity(ANY); };

  const applyAndSync = () => {
    const sp = {};
    if (listingType) sp.type = listingType;
    if (state !== ANY) sp.state = state;
    if (city !== ANY) sp.city = city;
    if (q) sp.q = q;
    setSearchParams(sp);
    fetchData();
  };

  const heading = listingType === "sold" ? "Sold Properties" : listingType === "rent" ? "Properties for Rent" : "Properties for Sale";

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-10">
      <h1 className="font-display text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">
        {heading}
      </h1>

      {/* Filter bar */}
      <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center gap-2 mb-4 text-slate-700">
          <SlidersHorizontal className="h-4 w-4" />
          <span className="text-sm font-semibold">Filters</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Select value={listingType} onValueChange={setListingType}>
            <SelectTrigger data-testid="filter-listing-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="buy">Buy</SelectItem>
              <SelectItem value="rent">Rent</SelectItem>
              <SelectItem value="sold">Sold</SelectItem>
            </SelectContent>
          </Select>
          <Select value={state} onValueChange={onStateChange}>
            <SelectTrigger data-testid="filter-state"><SelectValue placeholder="State" /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ANY}>All States/UTs</SelectItem>
              {INDIA_STATES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={city} onValueChange={setCity} disabled={state === ANY}>
            <SelectTrigger data-testid="filter-city"><SelectValue placeholder={state === ANY ? "Select state first" : "District/City"} /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={ANY}>All Districts</SelectItem>
              {districts.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={propType} onValueChange={setPropType}>
            <SelectTrigger data-testid="filter-prop-type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>All Types</SelectItem>
              {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={bhk} onValueChange={setBhk}>
            <SelectTrigger data-testid="filter-bhk"><SelectValue placeholder="Rooms (BHK)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any Rooms</SelectItem>
              {BHKS.map((b) => <SelectItem key={b} value={b}>{b} BHK</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={minBath} onValueChange={setMinBath}>
            <SelectTrigger data-testid="filter-bathrooms"><SelectValue placeholder="Bathrooms" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any Baths</SelectItem>
              {["1", "2", "3", "4"].map((b) => <SelectItem key={b} value={b}>{b}+ Bath</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={minCar} onValueChange={setMinCar}>
            <SelectTrigger data-testid="filter-car"><SelectValue placeholder="Car parking" /></SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Any Parking</SelectItem>
              {["0", "1", "2", "3"].map((c) => <SelectItem key={c} value={c}>{c}+ Car</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            type="button"
            data-testid="filter-ensuite"
            onClick={() => setEnsuite((v) => !v)}
            className={`flex items-center justify-center gap-2 rounded-md border text-sm font-medium h-10 transition-colors ${ensuite ? "bg-navy text-white border-navy" : "bg-white text-slate-600 border-slate-200 hover:border-navy"}`}
          >
            {ensuite ? <Check className="h-4 w-4" /> : <BedDouble className="h-4 w-4" />} Ensuite
          </button>
          <div className="flex gap-2 col-span-2 md:col-span-1">
            <Input
              data-testid="filter-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && applyAndSync()}
              placeholder="Search town, locality…"
            />
            <Button data-testid="filter-apply-btn" onClick={applyAndSync} className="bg-navy hover:bg-navy-dark shrink-0">
              <Search className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500" data-testid="results-count">
            {loading ? "Loading…" : `${properties.length} properties found`}
          </p>
          <div className="flex items-center bg-slate-100 rounded-lg p-1">
            <button
              data-testid="view-list-btn"
              onClick={() => setView("list")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "list" ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}
            ><LayoutGrid className="h-4 w-4" /> List</button>
            <button
              data-testid="view-map-btn"
              onClick={() => setView("map")}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${view === "map" ? "bg-white text-navy shadow-sm" : "text-slate-500"}`}
            ><MapIcon className="h-4 w-4" /> Map</button>
          </div>
        </div>
        {view === "map" ? (
          <PropertyMap properties={properties} />
        ) : !loading && properties.length === 0 ? (
          <div className="py-24 text-center">
            <Frown className="h-12 w-12 text-slate-300 mx-auto" />
            <p className="mt-4 text-slate-500">No properties match your filters. Try adjusting them.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {properties.map((p, i) => <PropertyCard key={p.id} property={p} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
